import type { RunwayColor } from "../config/palette";

export type AircraftKind = RunwayColor;

export interface AircraftSpec {
  readonly kind: AircraftKind;
  readonly label: string;
  /** Cruise speed in world units per second. */
  readonly speed: number;
  /** Body radius used for separation and the airborne halo. */
  readonly radius: number;
  /** Body length, drives the model scale and shadow. */
  readonly length: number;
  readonly color: number;
  /** Runway colours this aircraft is allowed to land on. */
  readonly allows: readonly RunwayColor[];
  /** Relative spawn frequency. */
  readonly weight: number;
}

export const AIRCRAFT_KINDS: readonly AircraftKind[] = Object.freeze([
  "yellow",
  "blue",
  "red",
]);

export const AIRCRAFT_SPECS: Readonly<Record<AircraftKind, AircraftSpec>> =
  Object.freeze({
    yellow: Object.freeze({
      kind: "yellow",
      label: "Yellow",
      speed: 7,
      radius: 2.4,
      length: 4.2,
      color: 0xfacc15,
      allows: Object.freeze(["yellow"] as const),
      weight: 3,
    }),
    blue: Object.freeze({
      kind: "blue",
      label: "Blue",
      speed: 8,
      radius: 2.6,
      length: 4.6,
      color: 0x3b82f6,
      allows: Object.freeze(["blue"] as const),
      weight: 3,
    }),
    red: Object.freeze({
      kind: "red",
      label: "Red",
      speed: 9,
      radius: 2.8,
      length: 5,
      color: 0xf05252,
      allows: Object.freeze(["red"] as const),
      weight: 2,
    }),
  });

/** Weighted pick driven by a caller supplied [0, 1) sample. */
export function pickAircraftKind(sample: number): AircraftKind {
  const total = AIRCRAFT_KINDS.reduce(
    (sum, kind) => sum + AIRCRAFT_SPECS[kind].weight,
    0,
  );
  let remaining = sample * total;
  for (const kind of AIRCRAFT_KINDS) {
    remaining -= AIRCRAFT_SPECS[kind].weight;
    if (remaining <= 0) {
      return kind;
    }
  }
  return "blue";
}
